import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, MapPin } from "lucide-react";

import { request } from "@/lib/api";

function composeAddress(parts) {
  return [parts.houseNo, parts.building, parts.street, parts.locality]
    .map((part) => (part || "").trim())
    .filter(Boolean)
    .join(", ");
}

/**
 * House number, building and street are typed; the area is looked up.
 *
 * The lookup is our own endpoint rather than the provider's, so the API key
 * stays on the server. When it isn't configured — or is having a bad day — the
 * field is still an ordinary text input and the form works exactly as it did
 * before, which is the whole point of not making it a special control.
 */
export function AddressAutocomplete({ id, value, onChange, onAddressSelect, placeholder, testId, token }) {
  const [houseNo, setHouseNo] = useState("");
  const [building, setBuilding] = useState("");
  const [street, setStreet] = useState("");
  const [locality, setLocality] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lookupOff, setLookupOff] = useState(false);
  const partsRef = useRef({ houseNo, building, street, locality });
  const onChangeRef = useRef(onChange);
  const onSelectRef = useRef(onAddressSelect);
  // Set when a suggestion is chosen, so the choice doesn't immediately trigger
  // a fresh search for the text it just inserted.
  const justPicked = useRef(false);
  const boxRef = useRef(null);

  useEffect(() => {
    onChangeRef.current = onChange;
    onSelectRef.current = onAddressSelect;
  }, [onChange, onAddressSelect]);

  const updatePart = useCallback((key, part) => {
    partsRef.current = { ...partsRef.current, [key]: part };
    onChangeRef.current(composeAddress(partsRef.current));
  }, []);

  useEffect(() => {
    if (justPicked.current) {
      justPicked.current = false;
      return undefined;
    }
    const term = locality.trim();
    if (lookupOff || term.length < 3) {
      setSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      setLoading(true);
      request(`/catalog/address-suggestions?q=${encodeURIComponent(term)}`, {}, token)
        .then((result) => {
          if (cancelled) return;
          // Stop asking for the rest of this session once we know the lookup
          // isn't set up — it will not start working mid-form.
          if (result.configured === false) setLookupOff(true);
          setSuggestions(result.suggestions || []);
          setOpen((result.suggestions || []).length > 0);
        })
        .catch(() => !cancelled && setSuggestions([]))
        .finally(() => !cancelled && setLoading(false));
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [locality, lookupOff, token]);

  // A click anywhere else means they're done with the list.
  useEffect(() => {
    function onDocumentClick(event) {
      if (boxRef.current && !boxRef.current.contains(event.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, []);

  function choose(suggestion) {
    justPicked.current = true;
    setLocality(suggestion.label);
    updatePart("locality", suggestion.label);
    setOpen(false);
    setSuggestions([]);
    // city and postcode go back to the caller so the form can fill its own
    // fields rather than asking for what was just picked.
    onSelectRef.current?.({
      placeId: suggestion.place_id || null,
      latitude: suggestion.latitude ?? null,
      longitude: suggestion.longitude ?? null,
      city: suggestion.city || "",
      postcode: suggestion.postcode || "",
      area: suggestion.area || "",
      label: suggestion.label,
    });
  }

  return (
    <div className="address-fields" data-testid={`${testId}-container`}>
      <div className="address-fields-row">
        <div>
          <label htmlFor={`${id}-house-no`}>House / Flat No.</label>
          <input id={`${id}-house-no`} value={houseNo} onChange={(event) => { setHouseNo(event.target.value); updatePart("houseNo", event.target.value); }} placeholder="e.g. 12A" data-testid={`${testId}-house-no`} />
        </div>
        <div>
          <label htmlFor={`${id}-building`}>Building / Society name</label>
          <input id={`${id}-building`} value={building} onChange={(event) => { setBuilding(event.target.value); updatePart("building", event.target.value); }} placeholder="e.g. Green Meadows" data-testid={`${testId}-building`} />
        </div>
      </div>

      <label htmlFor={`${id}-street`}>Street name</label>
      <input id={`${id}-street`} value={street} onChange={(event) => { setStreet(event.target.value); updatePart("street", event.target.value); }} placeholder="e.g. MG Road" data-testid={`${testId}-street`} />

      <label htmlFor={id}>Area / locality</label>
      <div className="address-autocomplete-wrap" ref={boxRef}>
        <div className="address-autocomplete">
          <MapPin size={17} aria-hidden="true" />
          <input
            id={id}
            value={locality}
            onChange={(event) => {
              setLocality(event.target.value);
              updatePart("locality", event.target.value);
              onSelectRef.current?.(null);
            }}
            onFocus={() => suggestions.length && setOpen(true)}
            placeholder={placeholder}
            autoComplete="off"
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            data-testid={testId}
          />
          {loading && <Loader2 size={15} className="address-spinner" aria-hidden="true" />}
        </div>
        {open && suggestions.length > 0 && (
          <ul className="address-suggestions" role="listbox" data-testid={`${testId}-suggestions`}>
            {suggestions.map((suggestion, index) => (
              <li key={`${suggestion.place_id || suggestion.label}-${index}`}>
                <button type="button" onClick={() => choose(suggestion)} data-testid={`${testId}-suggestion-${index}`}>
                  <MapPin size={14} aria-hidden="true" />
                  <span>{suggestion.label}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <small data-testid={`${testId}-hint`}>Search for the area or enter it manually — combined with the details above to form the full address.</small>
    </div>
  );
}
